import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SITE_URL } from './seoUtils';

const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_IMAGE_ALT = 'Rentasvet – prvi srpski sajt za rentiranje';
const DEFAULT_DESCRIPTION =
  'Rentasvet – prvi srpski sajt za rentiranje. Iznajmi vozila, alate, poslovne prostore, usluge i još mnogo toga. Bez provizije i posrednika. Direktna komunikacija vlasnika i zakupca.';
const DEFAULT_KEYWORDS =
  'rentiranje, iznajmljivanje, oglasi, vozila, alati, poslovni prostor, usluge, Srbija, bez provizije, rentasvet, zakup, najam';

/** Trim a string to maxLen chars without cutting a word in the middle. */
const trimDescription = (text, maxLen = 160) => {
  const clean = text?.replace(/<[^>]*>/g, '').trim() ?? '';
  if (clean.length <= maxLen) return clean;
  return clean.slice(0, maxLen).replace(/\s+\S*$/, '') + '…';
};

/**
 * Reusable SEO component — wraps react-helmet-async Helmet.
 *
 * @param {string}        title       - Page title (suffixed with " | Rentasvet")
 * @param {string}        description - Meta description (~160 chars ideal)
 * @param {string}        keywords    - Comma-separated keyword list
 * @param {string}        image       - Absolute URL for OG/Twitter image
 * @param {string}        imageAlt    - Alt text for OG image
 * @param {string}        url         - Canonical path (e.g. "/about")
 * @param {string}        type        - OG type: "website" | "article" | "product"
 * @param {string}        lang        - "sr" | "en"
 * @param {object|array}  jsonLd      - JSON-LD schema object(s) — single object or array
 * @param {boolean}       noIndex     - If true: noindex,nofollow
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  url,
  type = 'website',
  lang = 'sr',
  jsonLd = null,
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const safeDescription = trimDescription(description) || DEFAULT_DESCRIPTION;

  // Normalise jsonLd to array so we can render multiple <script> blocks
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {/* Document language */}
      <html lang={lang} />

      {/* Primary meta */}
      <title>{fullTitle}</title>
      <meta name='description' content={safeDescription} />
      {keywords && <meta name='keywords' content={keywords} />}
      <link rel='canonical' href={canonicalUrl} />

      {/* Robots */}
      {noIndex ? (
        <meta name='robots' content='noindex,nofollow' />
      ) : (
        <meta name='robots' content='index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1' />
      )}

      {/* Open Graph */}
      <meta property='og:site_name' content={SITE_NAME} />
      <meta property='og:type' content={type} />
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={safeDescription} />
      <meta property='og:image' content={image} />
      <meta property='og:image:alt' content={imageAlt} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:locale' content={lang === 'sr' ? 'sr_RS' : 'en_US'} />

      {/* Twitter / X Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={safeDescription} />
      <meta name='twitter:image' content={image} />
      <meta name='twitter:image:alt' content={imageAlt} />
      <meta name='twitter:site' content='@rentasvet' />

      {/* JSON-LD Structured Data — one <script> per schema */}
      {schemas.map((schema, i) => (
        <script key={i} type='application/ld+json'>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
